import { unzlibSync } from 'fflate';
import { type ByteSource, SliceSource } from '../bytes';
import { cryptTableEntry, decryptBlock, decryptBytes, fileKey, hashString, HashType, normalizePath } from './crypto';
import { explode } from './explode';
import { decompressHuffman } from './huffman';
import { decompressAdpcm } from './adpcm';

export const MPQ_FILE = {
  IMPLODE: 0x00000100,
  COMPRESS: 0x00000200,
  ENCRYPTED: 0x00010000,
  FIX_KEY: 0x00020000,
  SINGLE_UNIT: 0x01000000,
  DELETE_MARKER: 0x02000000,
  SECTOR_CRC: 0x04000000,
  EXISTS: 0x80000000,
} as const;

const COMP = { HUFFMAN: 0x01, ZLIB: 0x02, PKWARE: 0x08, BZIP2: 0x10, ADPCM_MONO: 0x40, ADPCM_STEREO: 0x80 } as const;

interface HashEntry {
  nameA: number;
  nameB: number;
  locale: number;
  block: number;
}

export interface BlockEntry {
  offset: number;
  csize: number;
  size: number;
  flags: number;
}

export class MpqError extends Error {}

/**
 * Read-only MPQ (format v0/v1) archive over a random-access source.
 * Files are located by name via the hash table; a `(listfile)` (if present) enables listing.
 */
export class MpqArchive {
  private readonly hashes: HashEntry[];
  private readonly hashMask: number;
  private listing: string[] | null = null;

  private constructor(
    private readonly src: ByteSource,
    readonly name: string,
    readonly sectorSize: number,
    hashes: HashEntry[],
    readonly blocks: BlockEntry[],
  ) {
    this.hashes = hashes;
    this.hashMask = hashes.length - 1;
  }

  /**
   * Opens an archive. If `scan` is set, the MPQ header is searched for at 512-byte boundaries
   * (archives embedded in self-extracting patch executables).
   */
  static async open(src: ByteSource, name = 'archive', scan = false): Promise<MpqArchive> {
    let base = -1;
    if (!scan) {
      base = 0;
    } else {
      const chunk = 1 << 20;
      for (let off = 0; off < src.size && base < 0; off += chunk) {
        const buf = await src.read(off, chunk + 32);
        for (let i = 0; i + 32 <= buf.length && i < chunk; i += 512) {
          if (isHeader(buf, i)) {
            base = off + i;
            break;
          }
        }
      }
      if (base < 0) throw new MpqError(`${name}: no MPQ header found`);
    }
    const head = await src.read(base, 32);
    if (!isHeader(head, 0)) throw new MpqError(`${name}: not an MPQ archive`);
    const dv = new DataView(head.buffer, head.byteOffset, 32);
    const sectorShift = dv.getUint16(14, true);
    const hashOff = dv.getUint32(16, true);
    const blockOff = dv.getUint32(20, true);
    const hashCount = dv.getUint32(24, true);
    const blockCount = dv.getUint32(28, true);
    const arch = new SliceSource(src, base, src.size - base);

    const hashWords = await readTable(arch, hashOff, hashCount * 4, hashString('(hash table)', HashType.FileKey));
    const hashes: HashEntry[] = new Array(hashCount);
    for (let i = 0; i < hashCount; i++) {
      hashes[i] = {
        nameA: hashWords[i * 4],
        nameB: hashWords[i * 4 + 1],
        locale: hashWords[i * 4 + 2] & 0xffff,
        block: hashWords[i * 4 + 3],
      };
    }
    const blockWords = await readTable(arch, blockOff, blockCount * 4, hashString('(block table)', HashType.FileKey));
    const blocks: BlockEntry[] = new Array(blockCount);
    for (let i = 0; i < blockCount; i++) {
      blocks[i] = {
        offset: blockWords[i * 4],
        csize: blockWords[i * 4 + 1],
        size: blockWords[i * 4 + 2],
        flags: blockWords[i * 4 + 3],
      };
    }
    return new MpqArchive(arch, name, 512 << sectorShift, hashes, blocks);
  }

  /** Locates the block entry for `path`, or null. Prefers the neutral locale. */
  findBlock(path: string): BlockEntry | null {
    const start = hashString(path, HashType.TableOffset) & this.hashMask;
    const a = hashString(path, HashType.NameA);
    const b = hashString(path, HashType.NameB);
    let fallback: BlockEntry | null = null;
    for (let n = 0, i = start; n < this.hashes.length; n++, i = (i + 1) & this.hashMask) {
      const h = this.hashes[i];
      if (h.block === 0xffffffff) break;
      if (h.nameA === a && h.nameB === b && h.block !== 0xfffffffe) {
        const blk = this.blocks[h.block];
        if (!blk || !(blk.flags & MPQ_FILE.EXISTS) || blk.flags & MPQ_FILE.DELETE_MARKER) continue;
        if (h.locale === 0) return blk;
        fallback ??= blk;
      }
    }
    return fallback;
  }

  has(path: string): boolean {
    return this.findBlock(path) !== null;
  }

  async read(path: string): Promise<Uint8Array> {
    const blk = this.findBlock(path);
    if (!blk) throw new MpqError(`${this.name}: file not found: ${path}`);
    return this.readBlock(blk, path);
  }

  async tryRead(path: string): Promise<Uint8Array | null> {
    const blk = this.findBlock(path);
    return blk ? this.readBlock(blk, path) : null;
  }

  /**
   * Random access to the file `path` without reading it whole (an archive inside an archive, as
   * Blizzard's installers keep them), or null if it is absent:
   *
   *  - stored (neither compressed nor encrypted): a slice of this archive's source; reads go
   *    straight to the outer file;
   *  - compressed or encrypted in sectors: each read decodes only the sectors it touches (the
   *    sector table is read once; the last few decoded sectors are kept);
   *  - compressed as a single unit: decoded whole on the first read and kept, so only small files
   *    (an executable, not a data archive) should be opened this way.
   */
  fileSource(path: string): ByteSource | null {
    const blk = this.findBlock(path);
    if (!blk) return null;
    if (!(blk.flags & (MPQ_FILE.COMPRESS | MPQ_FILE.IMPLODE | MPQ_FILE.ENCRYPTED))) return new SliceSource(this.src, blk.offset, blk.size);
    if (blk.flags & MPQ_FILE.SINGLE_UNIT) {
      let whole: Promise<Uint8Array> | null = null;
      return {
        size: blk.size,
        read: async (offset, length) => (await (whole ??= this.readBlock(blk, path))).subarray(offset, Math.min(blk.size, offset + length)),
      };
    }
    const key = blk.flags & MPQ_FILE.ENCRYPTED ? fileKey(path, blk.offset, blk.size, (blk.flags & MPQ_FILE.FIX_KEY) !== 0) : 0;
    return new SectorSource(this.src, { size: blk.size, csize: blk.csize, flags: blk.flags, key, sectorSize: this.sectorSize }, blk.offset);
  }

  /** The archive's `(listfile)` entries, if it has one. */
  async list(): Promise<string[]> {
    if (this.listing) return this.listing;
    const raw = await this.tryRead('(listfile)');
    if (!raw) return (this.listing = []);
    let text = '';
    for (let i = 0; i < raw.length; i++) text += String.fromCharCode(raw[i]);
    this.listing = text
      .split(/[\r\n;]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    return this.listing;
  }

  /**
   * Reads a block whose name is unknown. For encrypted compressed files the key is recovered
   * from the sector table, whose first entry is known plaintext (the table's own size).
   */
  async readBlockAnonymous(index: number): Promise<Uint8Array | null> {
    const blk = this.blocks[index];
    if (!blk || !(blk.flags & MPQ_FILE.EXISTS)) return null;
    if (!(blk.flags & MPQ_FILE.ENCRYPTED)) return this.readBlock(blk, '', 0);
    if (!(blk.flags & (MPQ_FILE.COMPRESS | MPQ_FILE.IMPLODE)) || blk.flags & MPQ_FILE.SINGLE_UNIT) return null;
    const sectors = Math.ceil(blk.size / this.sectorSize);
    const head = await this.src.read(blk.offset, 8);
    const dv = new DataView(head.buffer, head.byteOffset, 8);
    const enc0 = dv.getUint32(0, true);
    const enc1 = dv.getUint32(4, true);
    for (const extra of [0, 1]) {
      const plain0 = (sectors + 1 + extra) * 4;
      const key = recoverKey(enc0, plain0, enc1, blk.csize);
      if (key !== null) return this.readBlock(blk, '', (key + 1) >>> 0);
    }
    return null;
  }

  private async readBlock(blk: BlockEntry, path: string, forcedKey?: number): Promise<Uint8Array> {
    if (blk.size === 0) return new Uint8Array(0);
    const encrypted = (blk.flags & MPQ_FILE.ENCRYPTED) !== 0;
    const key = !encrypted
      ? 0
      : (forcedKey ?? fileKey(path, blk.offset, blk.size, (blk.flags & MPQ_FILE.FIX_KEY) !== 0));
    const raw = await this.src.read(blk.offset, blk.csize);
    const params: BlockParams = { size: blk.size, csize: blk.csize, flags: blk.flags, key, sectorSize: this.sectorSize };
    // Big compressed files (music, long speech) decode for a second or more: hand them to the
    // registered off-thread decoder when there is one.
    if (largeDecoder && blk.size >= largeDecoder.minSize && blk.flags & (MPQ_FILE.COMPRESS | MPQ_FILE.IMPLODE)) return largeDecoder.decode(raw, params);
    return decodeBlock(raw, params);
  }
}

/** What `decodeBlock` needs to turn a block's stored bytes into the file. */
export interface BlockParams {
  size: number;
  csize: number;
  flags: number;
  /** File key (0 when the block isn't encrypted). */
  key: number;
  sectorSize: number;
}

let largeDecoder: { minSize: number; decode: (raw: Uint8Array, p: BlockParams) => Promise<Uint8Array> } | null = null;

/** Registers a decoder (e.g. a Web Worker running `decodeBlock`) for blocks of `minSize` bytes or more. */
export function setLargeBlockDecoder(decode: ((raw: Uint8Array, p: BlockParams) => Promise<Uint8Array>) | null, minSize = 256 * 1024): void {
  largeDecoder = decode ? { minSize, decode } : null;
}

/** Decrypts and decompresses a block's stored bytes (pure; safe to run in a worker). */
export function decodeBlock(raw: Uint8Array, p: BlockParams): Uint8Array {
  const encrypted = (p.flags & MPQ_FILE.ENCRYPTED) !== 0;
  const compressed = (p.flags & (MPQ_FILE.COMPRESS | MPQ_FILE.IMPLODE)) !== 0;
  const { key, sectorSize } = p;
  if (p.flags & MPQ_FILE.SINGLE_UNIT) {
    const data = encrypted ? decryptBytes(raw, key) : raw;
    return compressed && p.csize < p.size ? decompressSector(data, p.size, p.flags) : data.slice(0, p.size);
  }
  if (!compressed) {
    // Uncompressed multi-sector file: sectors are contiguous, each encrypted with key + index.
    if (!encrypted) return raw.slice(0, p.size);
    const out = new Uint8Array(p.size);
    for (let s = 0, o = 0; o < p.size; s++, o += sectorSize) {
      const n = Math.min(sectorSize, p.size - o);
      out.set(decryptBytes(raw.subarray(o, o + n), (key + s) >>> 0), o);
    }
    return out;
  }

  const sectors = Math.ceil(p.size / sectorSize);
  const tableLen = sectors + 1 + (p.flags & MPQ_FILE.SECTOR_CRC ? 1 : 0);
  const tableBytes = raw.slice(0, tableLen * 4);
  const table = new Uint32Array(tableBytes.buffer, 0, tableLen);
  if (encrypted) decryptBlock(table, (key - 1) >>> 0);

  const out = new Uint8Array(p.size);
  for (let s = 0; s < sectors; s++) {
    const start = table[s];
    const end = table[s + 1];
    const outLen = Math.min(sectorSize, p.size - s * sectorSize);
    let sector = raw.subarray(start, end);
    if (encrypted) sector = decryptBytes(sector, (key + s) >>> 0);
    const data = sector.length < outLen ? decompressSector(sector, outLen, p.flags) : sector;
    out.set(data.subarray(0, outLen), s * sectorSize);
  }
  return out;
}

/** A sector-compressed or encrypted file read on demand: only the sectors a read touches are decoded. */
class SectorSource implements ByteSource {
  readonly size: number;
  private table: Promise<Uint32Array> | null = null;
  private readonly sectors = new Map<number, Promise<Uint8Array>>();
  private static readonly KEEP = 16;

  constructor(
    private readonly src: ByteSource,
    private readonly p: BlockParams,
    private readonly offset: number,
  ) {
    this.size = p.size;
  }

  async read(offset: number, length: number): Promise<Uint8Array> {
    const end = Math.min(this.size, offset + length);
    if (end <= offset) return new Uint8Array(0);
    const S = this.p.sectorSize;
    const first = Math.floor(offset / S);
    const last = Math.floor((end - 1) / S);
    const out = new Uint8Array(end - offset);
    for (let s = first; s <= last; s++) {
      const data = await this.sector(s);
      const from = Math.max(offset, s * S);
      const to = Math.min(end, s * S + data.length);
      out.set(data.subarray(from - s * S, to - s * S), from - offset);
    }
    return out;
  }

  private sector(s: number): Promise<Uint8Array> {
    let p = this.sectors.get(s);
    if (p) return p;
    p = this.decode(s);
    this.sectors.set(s, p);
    if (this.sectors.size > SectorSource.KEEP) this.sectors.delete(this.sectors.keys().next().value!);
    return p;
  }

  private async decode(s: number): Promise<Uint8Array> {
    const { size, flags, key, sectorSize } = this.p;
    const encrypted = (flags & MPQ_FILE.ENCRYPTED) !== 0;
    const outLen = Math.min(sectorSize, size - s * sectorSize);
    if (!(flags & (MPQ_FILE.COMPRESS | MPQ_FILE.IMPLODE))) {
      // Uncompressed sectors are contiguous; only the encryption is per sector.
      const raw = await this.src.read(this.offset + s * sectorSize, outLen);
      return encrypted ? decryptBytes(raw, (key + s) >>> 0) : raw;
    }
    const table = await (this.table ??= this.readTable());
    let sector = await this.src.read(this.offset + table[s], table[s + 1] - table[s]);
    if (encrypted) sector = decryptBytes(sector, (key + s) >>> 0);
    return sector.length < outLen ? decompressSector(sector, outLen, flags) : sector.subarray(0, outLen);
  }

  private async readTable(): Promise<Uint32Array> {
    const sectors = Math.ceil(this.p.size / this.p.sectorSize);
    const len = sectors + 1 + (this.p.flags & MPQ_FILE.SECTOR_CRC ? 1 : 0);
    const bytes = (await this.src.read(this.offset, len * 4)).slice();
    const table = new Uint32Array(bytes.buffer, 0, len);
    if (this.p.flags & MPQ_FILE.ENCRYPTED) decryptBlock(table, (this.p.key - 1) >>> 0);
    return table;
  }
}

/** Finds the key that decrypts `enc0` to `plain0`, validated by the next table entry. */
function recoverKey(enc0: number, plain0: number, enc1: number, csize: number): number | null {
  for (let b = 0; b < 256; b++) {
    const seed = (0xeeeeeeee + cryptTableEntry(0x400 + b)) >>> 0;
    const key = (((enc0 ^ plain0) >>> 0) - seed) >>> 0;
    if ((key & 0xff) !== b) continue;
    const pair = new Uint32Array([enc0, enc1]);
    decryptBlock(pair, key);
    if (pair[0] === plain0 && pair[1] > plain0 && pair[1] <= csize) return key;
  }
  return null;
}

function isHeader(b: Uint8Array, i: number): boolean {
  if (b[i] !== 0x4d || b[i + 1] !== 0x50 || b[i + 2] !== 0x51 || b[i + 3] !== 0x1a) return false;
  const hs = b[i + 4] | (b[i + 5] << 8) | (b[i + 6] << 16) | (b[i + 7] << 24);
  return hs === 32 || hs === 44 || hs === 68 || hs === 208;
}

async function readTable(src: ByteSource, offset: number, words: number, key: number): Promise<Uint32Array> {
  const bytes = (await src.read(offset, words * 4)).slice();
  if (bytes.length < words * 4) throw new MpqError('MPQ: truncated table');
  const t = new Uint32Array(bytes.buffer, 0, words);
  decryptBlock(t, key);
  return t;
}

function decompressSector(data: Uint8Array, outLen: number, flags: number): Uint8Array {
  if (flags & MPQ_FILE.IMPLODE) return explode(data, outLen);
  const mask = data[0];
  let buf = data.subarray(1);
  // Decompression runs in the reverse of the order Storm compressed in.
  if (mask & COMP.BZIP2) throw new MpqError('MPQ: bzip2 sectors are not supported');
  if (mask & COMP.PKWARE) buf = explode(buf, outLen);
  if (mask & COMP.ZLIB) buf = unzlibSync(buf);
  if (mask & COMP.HUFFMAN) buf = decompressHuffman(buf, outLen);
  if (mask & COMP.ADPCM_STEREO) buf = decompressAdpcm(buf, outLen, 2);
  if (mask & COMP.ADPCM_MONO) buf = decompressAdpcm(buf, outLen, 1);
  return buf;
}

export { normalizePath };
